import {hot} from 'react-hot-loader';
import React from 'react';
import Helmet from 'react-helmet';
import moment from 'moment';
import getRouteDefinitions, {getRoutes} from './routes/routes';
import {useLocation} from 'react-router';
import {useSelector} from 'react-redux';
import {TransitionGroup, CSSTransition} from 'react-transition-group';
import {getMetaTags} from './utils/Utils';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';
import {VisualizationsThemeProvider} from '@epic-visualizations/theme';
import {ThemeProvider as MUIThemeProvider} from '@material-ui/core/styles';
import FnGamesTourProvider from './pages/fortnite-games/FnGamesTourProvider';
import {withRouterProvider} from './routes/RouterProvider';
import SidebarPreviousDataProvider from './components/sidebar/SidebarPreviousDataProvider';
import AppErrorAlert from './components/error/AppErrorAlert';
import {ThemeProvider} from 'styled-components';
import {theme} from './overrides/theme/theme';
import {createEpicTheme} from './overrides/theme';
import {selectLocale, selectRTL} from './selectors';
import {RootState} from '.';

//@ts-ignore
import styles from './App.pcss';

// Use western arabic numbers
moment.defineLocale('ar', {
    parentLocale: 'ar',
    preparse: string => string,
    postformat: string => string
});

const epicTheme = createEpicTheme();
const styledTheme = {
    ...theme,
    muiTheme: epicTheme
};

interface Props {
    messages: object;
    getMessage: (code: string, args?: any[]) => string;
}

const App: React.FC<Props> = props => {
    const location = useLocation();
    const isRTL = useSelector<RootState, boolean>(selectRTL);
    const epicLocale = useSelector<RootState, string>(selectLocale);

    // makeStyles not propagating properly with useEffect
    epicTheme.direction = isRTL ? 'rtl' : 'ltr';
    styledTheme.muiTheme.direction = epicTheme.direction;

    // Change calendar week to start with Monday
    moment.updateLocale(moment.locale(epicLocale), {
        //@ts-ignore
        week: {
            dow: 1
        }
    });

    /**
     * Needed for accurate styles while feed
     * must support both embedded and global versions
     */
    const locationKey = location.pathname;
    const tags = getMetaTags(props.messages);
    const timeout = {
        enter: 1000,
        exit: 0
    };

    return (
        <MUIThemeProvider theme={epicTheme}>
            <ThemeProvider theme={styledTheme}>
                <VisualizationsThemeProvider>
                    <FnGamesTourProvider>
                        <SidebarPreviousDataProvider>
                            <div className={`${styles.app} global`}>
                                <Helmet
                                    meta={tags}
                                    title={props.getMessage('epic.affiliate.name')}
                                />
                                <div className={styles.container}>
                                    <TransitionGroup>
                                        <CSSTransition
                                            key={locationKey}
                                            timeout={timeout}
                                            classNames="fade"
                                            mountOnEnter={true}
                                            unmountOnExit={true}>
                                            <section>
                                                {getRoutes(getRouteDefinitions(), location)}
                                            </section>
                                        </CSSTransition>
                                    </TransitionGroup>
                                </div>
                                <AppErrorAlert key="appErrorAlert" />
                            </div>
                        </SidebarPreviousDataProvider>
                    </FnGamesTourProvider>
                </VisualizationsThemeProvider>
            </ThemeProvider>
        </MUIThemeProvider>
    );
};

export default hot(module)(withLocalization(withRouterProvider(App)));
